// src/lib/media.ts
// In the browser: empty string so callers issue same-origin requests
// and let the Next.js `/api/:path*` rewrite proxy them to API_BASE_HTTP.
// On the server: use the public URL directly. Mirrors src/lib/apiBase.ts.
export const API_BASE = typeof window === 'undefined'
  ? (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/+$/, '')
  : '';
const RAW_CDN_HOST = (process.env.NEXT_PUBLIC_CDN_HOSTNAME || '').replace(/\/+$/, '');
const RAW_CDN_BASE = (process.env.NEXT_PUBLIC_CDN_BASE || '').replace(/\/+$/, '');
const RAW_S3_BASE = (process.env.NEXT_PUBLIC_S3_BASE_URL || '').replace(/\/+$/, '');
const RAW_S3_HOST = (process.env.NEXT_PUBLIC_S3_HOSTNAME || '').replace(/\/+$/, '');
const USE_PROXY = (process.env.NEXT_PUBLIC_USE_PROXY || '').toLowerCase() === 'true';

function isSafePublicHost(host: string): boolean {
  try {
    const h = host.trim().toLowerCase();
    if (!h) return false;
    // Allow localhost and numeric IPv4 for dev
    if (h === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(h)) return true;
    // Reject bare docker/k8s service names (no dot)
    if (!h.includes('.')) return false;
    return true;
  } catch { return false; }
}

function sanitizeBase(input: string): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  try {
    // Normalize to absolute URL
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (!isSafePublicHost(url.hostname)) return '';
    // Preserve protocol and port when provided
    return `${url.protocol}//${url.host}`.replace(/\/+$/, '');
  } catch { return ''; }
}

const CDN_BASE = sanitizeBase(RAW_CDN_BASE) || sanitizeBase(RAW_CDN_HOST ? `https://${RAW_CDN_HOST}` : '');
const CDN_HOST = (() => { try { return CDN_BASE ? new URL(CDN_BASE).host : ''; } catch { return ''; } })();
const S3_BASE = sanitizeBase(RAW_S3_BASE) || sanitizeBase(RAW_S3_HOST ? `https://${RAW_S3_HOST}` : '');
const PREFER_S3_FOR_PROFILE = (() => {
  const v = (process.env.NEXT_PUBLIC_PREFER_S3_FOR_PROFILE || '').trim().toLowerCase();
  return v === '1' || v === 'true';
})();

/**
 * Normalizes a storage key or partial path to an absolute URL.
 * Prefers CDN, then S3 base. Falls back to root-relative if bases are unset.
 */
export function toAbsoluteMediaUrl(pathOrUrl?: string | null): string {
  if (!pathOrUrl) return '';
  // If absolute, optionally rewrite bad hosts to configured CDN base
  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const u = new URL(pathOrUrl);
      // Never rewrite AWS S3 presigned URLs; host rewrite invalidates signature
      if (/(^|&)X-Amz-/i.test(u.search.replace(/^\?/, ''))) {
        return pathOrUrl;
      }
      const p = u.pathname.replace(/^\/+/, '');
      if (CDN_HOST && /^(image-feed|thumbnails|video-thumbs|video-posters|videos\/posters|thumbs)\//i.test(p)) {
        if (!u.host || u.host.toLowerCase() !== CDN_HOST.toLowerCase()) {
          u.protocol = 'https:';
          u.host = CDN_HOST;
          return u.toString();
        }
      }
    } catch {}
    return pathOrUrl;
  }
  const cleaned = pathOrUrl.replace(/^\/+/, '');
  // Special-case: profile-media keys typically live on the primary S3 bucket (not always on CDN)
  if (/^profile-media\//i.test(cleaned) && S3_BASE) {
    return `${S3_BASE}/${cleaned}`;
  }
  // Prefer S3 for timestamp-style profile images only when explicitly enabled
  if (PREFER_S3_FOR_PROFILE && /^\d{12,20}(_(thumb|avatar))?\.(jpg|jpeg|png|webp)$/i.test(cleaned) && S3_BASE) {
    return `${S3_BASE}/${cleaned}`;
  }
  // Prefer CDN when configured; keep folder prefix in path
  if (CDN_BASE) return `${CDN_BASE}/${cleaned}`;
  // Fall back to S3 base/host if provided (supports keys like profile-media/, image-feed/, thumbnails/)
  if (S3_BASE) return `${S3_BASE}/${cleaned}`;
  // Final fallback: route through same-origin resolver which can proxy/presign safely
  return `/media/resolve?k=${encodeURIComponent(cleaned)}`;
}

/** Optionally append a cache-busting query in dev/troubleshooting. */
export function maybeCacheBust(url: string): string {
  if (!url) return url;
  // Use a stable parameter to avoid SSR/CSR hydration mismatch.
  if (process.env.NEXT_PUBLIC_IMAGE_NOCACHE !== '1') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}cb=1`;
}

/** Extracts an API token from NextAuth's session in the common shapes we use. */
export function getApiToken(session: any): string | undefined {
  const fromSession = (
    session?.accessToken ??
    session?.user?.accessToken ??
    session?.apiToken ??
    session?.user?.apiToken
  );
  if (fromSession) return fromSession as string;
  try {
    if (typeof window !== 'undefined') {
      const w: any = window as any;
      return (
        w.__AUTH_TOKEN__ ||
        localStorage.getItem('auth_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        undefined
      ) as string | undefined;
    }
  } catch {}
  return undefined;
}

/** Convenience: avatar URL with sensible default. */
export function toAvatarUrl(key?: string | null): string {
  if (!key || !String(key).trim()) return '/img/default-avatar.png';
  const raw = String(key).trim();
  // If absolute URL, try to rewrite avatars main -> avatar variant when recognizable; otherwise return as-is
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      // Only rewrite known avatar paths
      if (/\/avatars\//i.test(u.pathname) && !/_avatar\.[a-z0-9]+$/i.test(u.pathname)) {
        const base = u.pathname.replace(/\.[a-z0-9]+$/i, '');
        u.pathname = `${base}_avatar.jpg`;
        // Pass through absolute rewriter to optionally swap hosts (e.g., localhost -> CDN)
        return toAbsoluteMediaUrl(u.toString());
      }
    } catch { /* ignore parse issues, fall through */ }
    // Even if no avatar variant, allow host rewrite via helper
    return toAbsoluteMediaUrl(raw);
  }
  // Storage key: prefer small avatar variant if it follows our convention
  const cleaned = raw.replace(/^\/+/, '');
  // If already has _thumb or _avatar suffix, don't add another variant
  if (/_thumb\.|_avatar\./i.test(cleaned)) {
    return toAbsoluteMediaUrl(cleaned);
  }
  if (/^avatars\//i.test(cleaned)) {
    const base = cleaned.replace(/\.[a-z0-9]+$/i, '');
    return toAbsoluteMediaUrl(`${base}_avatar.jpg`);
  }
  return toAbsoluteMediaUrl(cleaned);
}

/** Convenience: thumbnail URL; falls back to absolute builder. */
export function toThumbUrl(key?: string | null): string {
  if (!key || !String(key).trim()) return '';
  const raw = String(key).trim();
  // Absolute: return as-is (caller should supply a thumb URL)
  if (/^https?:\/\//i.test(raw)) return raw;
  const cleaned = raw.replace(/^\/+/, '');
  // If it's an image-feed style path, just resolve via CDN base
  if (/^(image-feed|thumbnails|video-thumbs|video-posters|videos\/posters|thumbs)\//i.test(cleaned)) {
    return toAbsoluteMediaUrl(cleaned);
  }
  // Fallback: derive _avatar variant before extension (smaller, optimized for profiles)
  const base = cleaned.replace(/\.[a-z0-9]+$/i, '');
  return toAbsoluteMediaUrl(`${base}_avatar.jpg`);
}

/**
 * Returns a blurred image URL for age-gated content.
 * Used when guests view unsafe content to serve server-side blurred images
 * instead of CSS blur (which doesn't prevent access to original URLs).
 */
export function getBlurUrl(key?: string | null, variant: 'thumb' | 'avatar' = 'thumb'): string {
  if (!key || !String(key).trim()) return '';
  const raw = String(key).trim();

  // Extract S3 key from URL
  let s3Key = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      // Strip query parameters and get path
      s3Key = u.pathname.replace(/^\/+/, '');
    } catch {
      s3Key = raw;
    }
  } else {
    s3Key = raw.replace(/^\/+/, '');
  }

  // Strip variant suffixes (_thumb, _avatar) before adding _blurred
  // Example: 20250731122609660_thumb.jpg → 20250731122609660_blurred.jpg
  s3Key = s3Key.replace(/_(thumb|avatar)(\.[^.]+)$/, '_blurred$2');

  // If no variant suffix, add _blurred before extension
  // Example: image.jpg → image_blurred.jpg
  if (!s3Key.includes('_blurred')) {
    const lastDot = s3Key.lastIndexOf('.');
    if (lastDot === -1) {
      s3Key = s3Key + '_blurred';
    } else {
      s3Key = s3Key.substring(0, lastDot) + '_blurred' + s3Key.substring(lastDot);
    }
  }

  // Return CDN URL for blurred image
  return toAbsoluteMediaUrl(s3Key);
}

/**
 * Strips `_blurred` (and optional `_blurred_<timestamp>`) from an image URL so
 * authenticated users see the original.
 * ContentRescanWorker generates keys like `image_blurred_1706400000.jpg`;
 * the legacy pattern `image_blurred.jpg` is also handled.
 */
export function unblurUrl(url: string): string {
  if (!url) return url;
  return url.replace(/_blurred(_\d+)?(\.[a-z0-9]+(?:\?.*)?$)/i, '$2');
}
